import React, {
  useEffect,
  useState,
  lazy,
  useMemo,
  useRef,
  useCallback,
  Suspense,
} from "react";
import axios from "axios";
import {
  useOutletContext,
  useParams,
  Link,
} from "react-router-dom";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { TextField, MenuItem, CircularProgress } from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import EditStatus from "../../components/Modals/workstation/editStatus";
import toastr from "../../utils/toast";
import BulkAddLinkedinUrl from "../../components/Modals/workstation/BulkAddLinkedinUrl";
import ImportFromExcel from "../../components/Modals/workstation/ImportFromExcel";
import PptModal from "../../components/Modals/workstation/CreatePptOptions";
import ConsentModal from "../../components/Modals/workstation/ConsentModal";

import GenerateDossierModal from "../../components/Modals/Candidate/GenerateDossierModal";
import { Client_View } from "../../utils/sweetalert";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import GenerateDeckModal from "../../components/Modals/workstation/GenerateDeckModal";
import ClaudeGenerateDeckModal from "../../components/Modals/workstation/ClaudeGenerateDeckModal";
import GeneratedDocumentsModal from "../../components/Modals/workstation/GeneratedDocumentsModal";
import useSmartBack from "../../hooks/useSmartBack";
import { openInAppTab } from "../../utils/tabNavigation";
import OutreachDraft from "../../components/Modals/workstation/OutreachDraft";
import CandidateVideo, {
  VideoActionButton,
} from "../../components/Modals/workstation/CandidateVideo";
import SourceAnalysis from "../../components/Modals/workstation/SourceAnalysis";

// Grid columns that open for editing on one click; every other editable column
// needs a double click.
const SINGLE_CLICK_COLUMNS = ["colour", "major_status", "minor_status"];

// The status/country dropdown cells each loaded their own copy of the same lists, so
// opening this page fired hundreds of identical requests (and again on every re-render).
// The browser only runs ~6 requests per host at a time, so pages opened afterwards
// (e.g. going Back to the role page) sat in that queue behind them. Share one request.
const OPTIONS_CACHE_TTL_MS = 5 * 60 * 1000;
const optionsCache = new Map();
const getOptionsCached = (url, token) => {
  const hit = optionsCache.get(url);
  if (hit && Date.now() - hit.at < OPTIONS_CACHE_TTL_MS) return hit.promise;
  const promise = fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((res) => res.json())
    .catch((err) => {
      optionsCache.delete(url);
      throw err;
    });
  optionsCache.set(url, { at: Date.now(), promise });
  return promise;
};


// The three AI Actions icons, lifted from SAKETH-KB/DESIGN rather than
// approximated with MUI equivalents.
const svg = (children) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

const IconBot = () =>
  svg(
    <>
      <path d="M12 8V4H8" />
      <rect width="16" height="12" x="4" y="8" rx="2" />
      <path d="M2 14h2M20 14h2M15 13v2M9 13v2" />
    </>,
  );

const IconSend = () =>
  svg(
    <>
      <path d="M22 2 11 13" />
      <path d="M22 2 15 22l-4-9-9-4z" />
    </>,
  );

const IconFollowUp = () =>
  svg(
    <>
      <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
      <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
      <path d="M16 16h5v5" />
    </>,
  );

/** The two send channels: badge letter, colour, and how the tooltip says it. */
const SENT_RING = "#7e9dc4";
const SENT_CHANNELS = [
  { key: "linkedin", letter: "L", colour: SENT_RING, words: "on LinkedIn" },
  { key: "email", letter: "M", colour: "#32455d", words: "by mail" },
];

const fmtSentDay = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

/** "sent on LinkedIn 12 Oct, by mail 13 Oct" - or "" when nothing was sent. */
const sentPhrase = (marks) => {
  const parts = SENT_CHANNELS.filter((c) => marks?.[c.key] !== undefined).map(
    (c) => `${c.words} ${fmtSentDay(marks[c.key])}`.trim(),
  );
  return parts.length ? `sent ${parts.join(", ")}` : "";
};

/**
 * L / M over the icon's tip: sent on LinkedIn, by mail, or both.
 * `marks` is { linkedin?: iso, email?: iso } for one kind on one row.
 *
 * One ringed shape: a circle for one channel, a pill for both - steel-blue L
 * half, navy M half, rounded only at the outer ends.
 */
function SentBadges({ marks }) {
  const on = SENT_CHANNELS.filter((c) => marks?.[c.key] !== undefined);
  if (!on.length) return null;
  return (
    <span
      style={{
        position: "absolute",
        top: "-4px",
        left: "14px",
        display: "flex",
        border: `1px solid ${SENT_RING}`,
        borderRadius: "8px",
        overflow: "hidden",
        pointerEvents: "none",
      }}
    >
      {on.map((c) => (
        <span
          key={c.key}
          style={{
            width: "13px",
            height: "13px",
            background: c.colour,
            color: "#fff",
            fontFamily: "ui-monospace, Menlo, Consolas, monospace",
            fontSize: "9px",
            fontWeight: 700,
            lineHeight: "13px",
            textAlign: "center",
          }}
        >
          {c.letter}
        </span>
      ))}
    </span>
  );
}

/** The mockup's button: flat, square-ish, orange on hover. */
function AiActionButton({ title, onClick, children, sent }) {
  const [hover, setHover] = useState(false);
  const phrase = sentPhrase(sent);
  return (
    <Tooltip title={phrase ? `${title} · ${phrase}` : title}>
      <button
        aria-label={phrase ? `${title}, ${phrase}` : title}
        onClick={onClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          position: "relative",
          border: "none",
          cursor: "pointer",
          padding: "4px",
          display: "flex",
          borderRadius: "4px",
          background: hover ? "#f26b2a" : "transparent",
          color: hover ? "#fff" : "#333",
        }}
      >
        {children}
        <SentBadges marks={sent} />
      </button>
    </Tooltip>
  );
}
const modals = {
  LushaModal: lazy(
    () => import("../../components/Modals/Candidate/LushaModal"),
  ),
};

/**
 * A long note in a grid cell: one clipped line, the whole thing on hover.
 *
 * Reporting and team notes are read out of a consultant's call write-up, so
 * their length is whatever the consultant typed - measured at up to 2,060
 * characters in one cell, median 203. Rendered plainly, a single candidate
 * decides the row height for the entire grid.
 *
 * The tooltip keeps the line breaks the consultant wrote, because that is how
 * they separated the reporting line from the team.
 */
const ClippedNote = ({ value }) => {
  const text = (value ?? "").toString().trim();
  if (!text) return null;

  return (
    <Tooltip
      title={<span style={{ whiteSpace: "pre-wrap" }}>{text}</span>}
      placement="top-start"
    >
      <span
        style={{
          display: "block",
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          cursor: "help",
        }}
      >
        {text}
      </span>
    </Tooltip>
  );
};

/**
 * Consultant Notes: the viewer's own note from AI Sourcing Analysis, and how
 * many others have written one. Others' notes are read on hover, never shown
 * in the cell as if they were the viewer's - editing the cell changes only
 * the viewer's own note.
 */
const NOTE_TIP_CHARS = 800;
const clipForTip = (text) =>
  text.length > NOTE_TIP_CHARS
    ? `${text.slice(0, NOTE_TIP_CHARS)}… (the rest in AI Sourcing Analysis)`
    : text;
const notesByOthers = (n) => `${n} note${n === 1 ? "" : "s"} by others`;

const ConsultantNoteCell = ({ row }) => {
  const mine = (row.original.consultant_notes ?? "").toString().trim();
  const savedAt = row.original.consultant_notes_saved_at;
  const others = row.original.consultant_notes_others || [];
  if (!mine && !others.length) return null;

  const tip = (
    <span style={{ whiteSpace: "pre-wrap" }}>
      {mine && (
        <>
          <b>Your note{savedAt ? ` (${savedAt})` : ""}</b>
          {"\n"}
          {clipForTip(mine)}
        </>
      )}
      {others.map((n, i) => (
        <React.Fragment key={i}>
          {mine || i > 0 ? "\n\n" : ""}
          <b>
            {n.by_user || "A consultant"}
            {n.saved_at ? ` (${n.saved_at})` : ""}
          </b>
          {"\n"}
          {clipForTip(String(n.body || "").trim())}
        </React.Fragment>
      ))}
    </span>
  );

  return (
    <Tooltip title={tip} placement="top-start">
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          maxWidth: "100%",
          cursor: "help",
        }}
      >
        {mine ? (
          <span
            style={{
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {mine}
          </span>
        ) : (
          <span style={{ color: "#8a8a8a", fontStyle: "italic" }}>
            {notesByOthers(others.length)}
          </span>
        )}
        {mine && others.length > 0 && (
          <span
            aria-label={notesByOthers(others.length)}
            style={{
              flexShrink: 0,
              fontSize: "11px",
              color: "#555",
              background: "#eef1f5",
              borderRadius: "8px",
              padding: "0 6px",
            }}
          >
            +{others.length}
          </span>
        )}
      </span>
    </Tooltip>
  );
};

/**
 * Editing one of those notes in the grid.
 *
 * Multiline, unlike every other editable cell here. These hold paragraphs -
 * the reporting line on one line, the team on the next - and a single-line
 * box would let someone flatten a 2,000-character note without noticing.
 *
 * Enter inserts a newline rather than saving, for the same reason. Saving is
 * on blur, and Escape abandons the edit.
 */
const NoteEdit = ({ cell, table, onSave, saving }) => {
  const original = cell.getValue() ?? "";
  const [value, setValue] = React.useState(original);

  return (
    <div style={{ position: "relative" }}>
      <TextField
        fullWidth
        multiline
        minRows={3}
        maxRows={12}
        size="small"
        autoFocus
        value={value}
        disabled={saving}
        placeholder="Nothing recorded"
        onChange={(e) => setValue(e.target.value)}
        onBlur={async () => {
          if (value !== original) await onSave(cell, value);
          table.setEditingCell(null);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.preventDefault();
            table.setEditingCell(null);
          }
        }}
      />
      {saving && (
        <CircularProgress
          size={18}
          sx={{ position: "absolute", right: 6, top: 10 }}
        />
      )}
    </div>
  );
};

export default function WorkstationView() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const APP_PINE_BASE_URL = process.env.REACT_APP_PINE_BASE_URL;
  const REACT_APP_BASE_URL_BACK = process.env.REACT_APP_BASE_URL_BACK;
  const { setGlobalLoading, token } = useOutletContext();
  const { id } = useParams();
  const majorChangedRef = useRef({});
  const [data, setData] = useState([]);
  const [CandidateData, setCandidateData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingCell, setSavingCell] = useState(null);
  const [savedCells, setSavedCells] = useState({});
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateStatus, setCandidateStatus] = useState([]);
  const [loadingCandidateStatus, setLoadingCandidateStatus] = useState(false);
  const [showEditStatusModal, setShowEditStatusModal] = useState(false);
  const [selectedMinorStatus, setSelectedMinorStatus] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [roleDetails, setRoleDetails] = useState({});
  const [BulkAddLinkedinUrlModel, setBulkAddLinkedinUrlModel] = useState(false);
  const [importFromExcelModal, setImportFromExcelModal] = useState(false);
  const [showPptModal, setShowPptModal] = useState(false);
  const [showDeckModal, setShowDeckModal] = useState(false);
  const [consentCandidateid, setConsentCandidateid] = useState(null);
  const [consentModal, setConsentModal] = useState(false);

  const [majorStatusOptions, setMajorStatusOptions] = useState([]);
  const [minorStatusOptions, setMinorStatusOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [addedByOptions, setAddedByOptions] = useState([]);

  const [showDossierModal, setShowDossierModal] = useState(false);
  const [columnSizing, setColumnSizing] = useState({});
  const [columnPinning, setColumnPinning] = useState({
    left: ["candidateID", "id", "name"],
    right: [],
  });
  const [wrapText, setWrapText] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [showDecksModal, setShowDecksModal] = useState(false);
  const [isLushaLoading, setIsLushaLoading] = useState(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [lushaPhone, setLushaPhone] = useState(null);
  const [lushaEmail, setLushaEmail] = useState(null);
  const [lastChecked, setLastChecked] = useState(null);
  const [lushaFlag, setLushaFlag] = useState(null);
  const [lushaCandidateId, setLushaCandidateId] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const OPTIONAL_COLUMNS = [
    "email",
    "phone",
    "designation",
    "company",
    "prev_company",
    "major_industry",
    "country",
    "city",
    "experience_years",
    "current_ctc",
    "preferred_location",
    "background",
    "skills",
    "potential",
    "motivation_to_move",
    "created",
    "brief_comments",
    "Evaluation_comments",
    // Optional, and off unless asked for. A column left out of this list can
    // never be hidden, and these two are empty for almost everyone - they are
    // filled only for candidates whose call notes have been read.
    "reporting_to_notes",
    "team_structure_notes",
    "consultant_notes",
  ];

  const DEFAULT_VISIBLE_COLUMNS = [
    "email",
    "phone",
    "designation",
    "company",
    "consultant_notes",
  ];

  const DEFAULT_VISIBILITY = OPTIONAL_COLUMNS.reduce(
    (acc, k) => ({
      ...acc,
      [k]: DEFAULT_VISIBLE_COLUMNS.includes(k),
    }),
    {},
  );

  const [columnVisibility, setColumnVisibility] = useState(DEFAULT_VISIBILITY);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/workstation/get_view_setting/${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        const row = res.data;

        // No saved settings
        if (!row) {
          setColumnVisibility(DEFAULT_VISIBILITY);
          setColumnSizing({});
          setColumnPinning({
            left: ["candidateID", "id", "name"],
            right: [],
          });
          setWrapText(false);
        } else {
          const parsed = row.view_setting ? JSON.parse(row.view_setting) : {};

          // =========================
          // COLUMN VISIBILITY
          // =========================

          if (row.pagination === "data-full") {
            setColumnVisibility(
              OPTIONAL_COLUMNS.reduce(
                (acc, key) => ({
                  ...acc,
                  [key]: true,
                }),
                {},
              ),
            );
          } else if (Array.isArray(parsed.visibleColumns)) {
            const visible = new Set(parsed.visibleColumns);

            // A column the saved layout has never seen - added to PINE after
            // it was saved - takes its default, so a new default-on column
            // shows up once for everyone. Hidden after that, it stays hidden.
            // Layouts saved before knownColumns existed knew every column
            // except these.
            const ADDED_LATER = ["consultant_notes"];
            const known = new Set(
              Array.isArray(parsed.knownColumns)
                ? parsed.knownColumns
                : OPTIONAL_COLUMNS.filter((k) => !ADDED_LATER.includes(k)),
            );

            setColumnVisibility(
              OPTIONAL_COLUMNS.reduce(
                (acc, key) => ({
                  ...acc,
                  [key]: known.has(key)
                    ? visible.has(key)
                    : DEFAULT_VISIBLE_COLUMNS.includes(key),
                }),
                {},
              ),
            );
          } else {
            // No visibility preference saved yet
            setColumnVisibility(DEFAULT_VISIBILITY);
          }

          // =========================
          // COLUMN SIZING
          // =========================

          setColumnSizing(parsed.columnSizing || {});

          // =========================
          // COLUMN PINNING
          // =========================

          const savedPinning = parsed.columnPinning || {};

          setColumnPinning({
            left: [
              "candidateID",
              "id",
              "name",
              ...(savedPinning.left || []).filter(
                (columnId) => !["candidateID", "id", "name"].includes(columnId),
              ),
            ],

            right: (savedPinning.right || []).filter(
              (columnId) => !["candidateID", "id", "name"].includes(columnId),
            ),
          });

          // =========================
          // TEXT WRAPPING
          // =========================

          setWrapText(!!parsed.wrapText);
        }
      } catch (err) {
        console.error("Failed to load view setting", err);
      } finally {
        setSettingsLoaded(true);
      }
    };

    loadSettings();
  }, [id, token]);

  const handleColumnPinningChange = useCallback((updater) => {
    setColumnPinning((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;

      return {
        ...next,

        // These columns MUST always remain pinned left
        left: [
          "candidateID",
          "id",
          "name",

          ...(next.left || []).filter(
            (columnId) => !["candidateID", "id", "name"].includes(columnId),
          ),
        ],

        // These columns can never be moved to right
        right: (next.right || []).filter(
          (columnId) => !["candidateID", "id", "name"].includes(columnId),
        ),
      };
    });
  }, []);

  const saveTimeout = useRef(null);

  useEffect(() => {
    if (!settingsLoaded) return;

    if (saveTimeout.current) {
      clearTimeout(saveTimeout.current);
    }

    saveTimeout.current = setTimeout(async () => {
      const allVisible = OPTIONAL_COLUMNS.every((k) => columnVisibility[k]);

      try {
        await axios.post(
          `${API_BASE_URL}/workstation/save_view_setting`,
          {
            role_id: id,

            pagination: allVisible ? "data-full" : "data",

            view_setting: JSON.stringify({
              visibleColumns: allVisible
                ? []
                : OPTIONAL_COLUMNS.filter((k) => columnVisibility[k]),

              // Which columns existed when this was saved - so one added
              // later can be told apart from one the user hid.
              knownColumns: OPTIONAL_COLUMNS,

              columnSizing,

              columnPinning: {
                left: columnPinning.left || [],
                right: columnPinning.right || [],
              },

              wrapText,
            }),
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
      } catch (err) {
        console.error("Failed to save view setting", err);
      }
    }, 600);

    return () => {
      if (saveTimeout.current) {
        clearTimeout(saveTimeout.current);
      }
    };
  }, [
    columnVisibility,
    columnSizing,
    columnPinning,
    wrapText,
    settingsLoaded,
    id,
    token,
  ]);

  const fetchCandidateStatus = async (candidateFilterId) => {
    setLoadingCandidateStatus(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/workstation/get_candidate_status/${candidateFilterId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setCandidateStatus(res.data?.candidate_status_list || []);
    } catch (err) {
      console.error("Failed to fetch candidate status:", err);
    } finally {
      setLoadingCandidateStatus(false);
    }
  };

  async function get_minor_status_details(name, major_status) {
    try {
      const res = await axios.post(
        `${API_BASE_URL}/workstation/get_candidate_status_detail`,
        {
          minor_status: name,
          major_status: major_status,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const data = res.data || [];
      return data;
    } catch (err) {
      console.error("Failed to fetch candidate status:", err);
    }
  }

  useEffect(() => {
    setMajorStatusOptions([...new Set(data.map((d) => d.major_status))]);

    setMinorStatusOptions([...new Set(data.map((d) => d.minor_status))]);

    setCountryOptions([
      ...new Set(
        data
          .map((d) => d.country)
          .filter((country) => country !== null && country !== ""),
      ),
    ]);

    setAddedByOptions([...new Set(data.map((d) => d.added_by))]);
  }, [data]);

  const ALLOWED_COLORS = [
    "#EF4444",
    "#F97316",
    "#FACC15",
    "#22C55E",
    "#14B8A6",
    "#0EA5E9",
    "#6366F1",
    "#8B5CF6",
    "#EC4899",
    "#6B7280",
  ];

  const COLOR_MAP = {
    "#EF4444": "Red",
    "#F97316": "Orange",
    "#FACC15": "Yellow",
    "#22C55E": "Green",
    "#14B8A6": "Teal",
    "#0EA5E9": "Sky Blue",
    "#6366F1": "Indigo",
    "#8B5CF6": "Purple",
    "#EC4899": "Pink",
    "#6B7280": "Gray",
  };

  const openFile = async (mainFolder, subFolder, filename) => {
    const response = await fetch(
      `${REACT_APP_BASE_URL_BACK}files/${mainFolder}/media/${subFolder}/${filename}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    if (!response.ok) {
      console.error("Failed to fetch file");
      return;
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    window.open(url, "_blank");
  };

  const handleDelete = async (candidateFilterId) => {
    Client_View({
      title: "Are you sure?",
      text: "This candidate will be deleted from the search map.",
      icon: "warning",
      confirmButtonText: "Yes, Delete",

      confirmCallback: async () => {
        try {
          await axios.post(
            `${API_BASE_URL}/workstation/delete/${candidateFilterId}/${id}`,
            {},
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );

          toastr.success("Candidate deleted successfully");

          // remove row instantly
          setData((prev) => prev.filter((row) => row.id !== candidateFilterId));
        } catch (error) {
          console.error("Delete error:", error);
          toastr.error("Failed to delete candidate");
        }
      },

      cancleCallback: () => {
        console.log("Delete cancelled");
      },
    });
  };

  const fetchLushaStatus = async (candidate_id) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/candidates/lusha_status/${candidate_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.status === 200 && response.data.data) {
        setShowPhoneModal(true);
        const { lusha_status_id, phone, created, updated, email } =
          response.data.data;

        setLushaFlag(Number(lusha_status_id));
        setLushaPhone(phone);
        setLushaEmail(email); // optional if you want to display/copy email
        setLastChecked(updated || created); // 👈 important for 6-month logic
      }
    } catch (error) {
      console.error("❌ Error fetching Lusha status:", error);
    }
  };
  useEffect(() => {
    fetchLushaStatus(lushaCandidateId);
  }, [lushaCandidateId]);

  // { kind: "outreach" | "followup", candidate } — one panel serves both.
  const [draftFor, setDraftFor] = useState(null);

  // The row whose video icon was clicked: upload, "submitted" or "ready".
  const [videoFor, setVideoFor] = useState(null);

  // Who on this role was sent outreach / follow-up, and on which channel -
  // the L / M badges. Loaded on its own: if it fails the grid is unaffected.
  const [sentMap, setSentMap] = useState({});

  useEffect(() => {
    let live = true;
    setSentMap({});
    axios
      .get(`${API_BASE_URL}/workstation/sent_channels/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (live) setSentMap(res.data?.sent || {});
      })
      .catch((err) => {
        console.error("[sent_channels] failed:", err.response?.data || err.message);
      });
    return () => {
      live = false;
    };
  }, [API_BASE_URL, id, token]);

  // Each candidate's latest video on this role, { "<candidate_id>": status }:
  // the ✓ on a ready one, the ! on a failed one. Loaded on its own, like
  // sentMap; if it fails the grid is unaffected.
  const [videoMap, setVideoMap] = useState({});
  const videoMapRef = useRef({});

  const loadVideos = useCallback(() => {
    axios
      .get(`${API_BASE_URL}/video/jobs`, {
        params: { role_id: id },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const next = {};
        const reasons = {};
        for (const v of res.data || []) {
          next[v.candidate_id] = v.status;
          reasons[v.candidate_id] = v.error;
        }
        const endedAs = (status) =>
          Object.keys(next).filter(
            (c) =>
              next[c] === status &&
              ["queued", "running"].includes(videoMapRef.current[c]),
          );
        const finished = endedAs("done");
        if (finished.length) {
          toastr.success(
            finished.length === 1
              ? "A candidate video is ready."
              : `${finished.length} candidate videos are ready.`,
          );
        }
        // One failure says why (Node's own wording, e.g. the dossier is
        // someone else's); several just count, and each row's ! has its reason.
        const failed = endedAs("failed");
        if (failed.length) {
          toastr.error(
            failed.length === 1
              ? reasons[failed[0]] || "A candidate video could not be made."
              : `${failed.length} candidate videos could not be made.`,
          );
        }
        videoMapRef.current = next;
        setVideoMap(next);
      })
      .catch((err) => {
        console.error("[video] list failed:", err.response?.data || err.message);
      });
  }, [API_BASE_URL, id, token]);

  useEffect(() => {
    videoMapRef.current = {};
    setVideoMap({});
    loadVideos();
  }, [loadVideos]);

  // While a video on this role is being made, look again every 20 s, so the ✓
  // appears without a reload. Each look is also what brings a finished video
  // into PINE. Stops when nothing is in progress.
  const videoPending = Object.values(videoMap).some(
    (s) => s === "queued" || s === "running",
  );
  useEffect(() => {
    if (!videoPending) return undefined;
    const timer = setInterval(loadVideos, 20000);
    return () => clearInterval(timer);
  }, [videoPending, loadVideos]);

  /** A send was just recorded in the panel - badge the row without a reload. */
  const markSent = useCallback((candidate_id, kind, channel, sent_at) => {
    setSentMap((m) => ({
      ...m,
      [candidate_id]: {
        ...m[candidate_id],
        [kind]: { ...m[candidate_id]?.[kind], [channel]: sent_at },
      },
    }));
  }, []);

  // The seam. Each of the three branches gets replaced in turn, and nothing
  // else in the grid changes when they are.
  const handleAiAction = useCallback((kind, candidate) => {
setDraftFor({ kind, candidate });
  }, []);

  const columns = useMemo(
    () => [
      {
        id: "candidateID",
        header: (
          <input
            type="checkbox"
            onChange={(e) => {
              const checked = e.target.checked;
              // toggle all checkboxes
              if (checked) {
                setSelectedRows(data.map((row) => row.candidate_id)); // store all IDs
              } else {
                setSelectedRows([]);
              }
            }}
            checked={selectedRows.length === data.length && data.length > 0}
          />
        ),
        Cell: ({ row }) => (
          <input
            type="checkbox"
            checked={selectedRows.includes(row.original.candidate_id)}
            onChange={(e) => {
              const checked = e.target.checked;
              const id = row.original.candidate_id;
              setSelectedRows((prev) => {
                const updated = checked
                  ? [...prev, id]
                  : prev.filter((r) => r !== id);

                console.log("Selected Rows:", updated);
                return updated;
              });
            }}
          />
        ),
        enableSorting: false,
        enableColumnActions: false,
        enableHiding: false,
        enablePinning: false,
        size: 40,
      },
      {
        accessorKey: "id",
        header: "ID",
        enableEditing: false,
        enableColumnFilter: true,
        enableHiding: false,
        enablePinning: false,
        Cell: ({ cell }) => {
          const gdpr = cell.row.original.gdpr_status;
          let gdpr_staus = "";
          let gdpr_icon = "";
          let onclick = false;
          const linkdin_url = cell.row.original.linkdin_url;
          const no_poach = cell.row.original.no_poach;
          const checkurl = linkdin_url?.split("://");
          const files = cell.row.original.files;
          const is_lusha = cell.row.original.is_lusha;
          let Lurl = "";
          let color = "";
          if (gdpr === "GDPR") {
            color = "orange";
            gdpr_staus = "Under GDPR";
            gdpr_icon = "fa-user-shield";
            onclick = true;
          } else if (gdpr === "rejected") {
            color = "red";
            gdpr_staus = "opt-out";
            gdpr_icon = "fa-lock";
          } else if (gdpr === "sent") {
            color = "blue";
            gdpr_staus = "consent requested";
            gdpr_icon = "fa-paper-plane";
            onclick = true;
          } else if (gdpr === "approved") {
            color = "green";
            gdpr_staus = "consent received";
            gdpr_icon = "fa-shield-heart";
          } else {
            color = "black";
            gdpr_staus = "Expired";
            gdpr_icon = "fa-user-clock";
          }

          if (checkurl && (checkurl[0] === "https" || checkurl[0] === "http")) {
            Lurl = linkdin_url;
          } else {
            Lurl = "http://" + linkdin_url;
          }
          return (
            <>
              <span>{cell.row.original.id}</span>
              {gdpr !== null && (
                <i
                  onClick={() => {
                    if (onclick) {
                      setConsentCandidateid(cell.row.original.candidate_id);
                      setConsentModal(true);
                    }
                  }}
                  className={`fa-solid ${gdpr_icon} ms-1`}
                  style={{
                    color: color,
                    ...(onclick && { cursor: "pointer" }),
                  }}
                  title={gdpr_staus}
                ></i>
              )}

              {linkdin_url !== null && (
                <a
                  href={Lurl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ cursor: "pointer" }}
                  className="ms-1"
                >
                  <i className="fa-brands fa-linkedin"></i>
                </a>
              )}
              {no_poach === 1 && (
                <i
                  className="ms-1 fa fa-ban text-danger"
                  title="No Poach Company"
                ></i>
              )}

              {files &&
                files.map((file, index) =>
                  file.documenttype_id === 1 ? (
                    <a
                      key={index}
                      onClick={() =>
                        openFile("candidates", "resume", file.savename)
                      }
                      title="Resume"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ cursor: "pointer" }}
                      className="ms-1"
                    >
                      <i className="fa-solid fa-r"></i>
                    </a>
                  ) : file.documenttype_id === 2 ? (
                    <a
                      key={index}
                      onClick={() =>
                        openFile("candidates", "dossiers", file.savename)
                      }
                      title="Dossier"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ cursor: "pointer" }}
                      className="ms-1"
                    >
                      <i className="fa-solid fa-d"></i>
                    </a>
                  ) : file.documenttype_id === 9 ? (
                    <a
                      key={index}
                      onClick={() =>
                        openFile("candidates", "others", file.savename)
                      }
                      title="Reference Check"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ cursor: "pointer" }}
                      className="ms-1"
                    >
                      RC
                    </a>
                  ) : null,
                )}
              {is_lusha === "1" && (
                <>
                  <a
                    onClick={() => {
                      setLushaCandidateId(cell.row.original.candidate_id);
                      setCandidateData(cell.row.original);
                    }}
                    tooltip="Verfiy Contact"
                    style={{ cursor: "pointer" }}
                    className="ms-1 text-primary"
                  >
                    <i class="fa-regular fa-address-book"></i>
                  </a>
                </>
              )}
            </>
          );
        },
      },
      {
        accessorKey: "name",
        header: "Name",
        enableEditing: true,
        enableColumnFilter: true,
        enableHiding: false,
        enablePinning: false,
        Cell: ({ cell }) => {
          return (
            <>
              <div style={{ display: "flex", alignItems: "center" }}>
                <a
                  href={`/candidates/view/${cell.row.original.candidate_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => {
                    event.preventDefault();
                    openInAppTab(
                      `/candidates/view/${cell.row.original.candidate_id}`,
                    );
                  }}
                >
                  {cell.row.original.name}
                </a>
                <a
                  href={`/candidates/viewlog/${cell.row.original.candidate_id}`} onClick={(e) => { e.preventDefault(); openInAppTab(`/candidates/viewlog/${cell.row.original.candidate_id}`); }}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="View Logs"
                  style={{ cursor: "pointer" }}
                >
                  <i className="fa-solid fa-circle-info ms-1"></i>
                </a>
              </div>
            </>
          );
        },
        Edit: ({ cell, table }) => {
          const cellKey = `${cell.row.original.id}-${cell.column.id}`;
          const isSaving = savingCell === cellKey;
          const isSaved = !!savedCells[cellKey];
          const [error, setError] = React.useState("");
          return (
            <div style={{ position: "relative" }}>
              <TextField
                fullWidth
                size="small"
                autoFocus
                defaultValue={cell.getValue()}
                disabled={isSaving}
                onBlur={async (e) => {
                  const newValue = e.target.value;
                  if (newValue !== cell.getValue()) {
                    setError("");
                    setSavingCell(cellKey);
                    await handleSaveCell(cell, newValue);
                    setSavingCell(null);
                    setSavedCells((prev) => ({ ...prev, [cellKey]: true }));
                  }
                  table.setEditingCell(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === "Escape")
                    e.currentTarget.blur();
                }}
              />

              {isSaving && (
                <CircularProgress
                  size={18}
                  sx={{
                    position: "absolute",
                    right: 6,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              )}

              {isSaved && !isSaving && (
                <CheckCircle
                  color="success"
                  fontSize="small"
                  sx={{
                    position: "absolute",
                    right: 6,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "colour",
        header: "Colour",
        size: 50,
        filterVariant: "select",
        filterSelectOptions: Object.values(COLOR_MAP),
        filterFn: (row, columnId, filterValue) => {
          return COLOR_MAP[row.getValue(columnId)] === filterValue;
        },
        enableEditing: true,

        // 🔵 VIEW MODE → circle only
        Cell: ({ cell }) => {
          const color = cell.getValue();

          return (
            <div
              title={COLOR_MAP[color] || "No colour"}
              style={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                backgroundColor: color || "transparent",
                border: "1px solid #999",
                margin: "auto",
              }}
            />
          );
        },

        // ✏️ EDIT MODE → predefined palette
        Edit: ({ cell, row, table }) => {
          const currentColor = cell.getValue();
          const wrapperRef = React.useRef(null);

          const saveAndClose = (color) => {
            // 1️⃣ update UI immediately
            table.options.meta.updateData(row.index, cell.column.id, color);

            // 2️⃣ save to DB
            table.options.meta.saveColour(row.original.id, color);

            // 3️⃣ close editor AFTER render flush
            requestAnimationFrame(() => {
              table.setEditingCell(null);
            });
          };

          // 👆 OUTSIDE CLICK → cancel editing
          React.useEffect(() => {
            const handleClickOutside = (e) => {
              if (
                wrapperRef.current &&
                !wrapperRef.current.contains(e.target)
              ) {
                table.setEditingCell(null);
              }
            };

            document.addEventListener("mousedown", handleClickOutside);
            return () =>
              document.removeEventListener("mousedown", handleClickOutside);
          }, []);

          return (
            <div
              ref={wrapperRef}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexWrap: "wrap",
                maxWidth: 130,
              }}
            >
              {/* 🔵 DB COLOR (if not in allowed list) */}
              {currentColor && !ALLOWED_COLORS.includes(currentColor) && (
                <div
                  title={COLOR_MAP[currentColor] || currentColor}
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    backgroundColor: currentColor,
                    border: "2px solid #000",
                  }}
                />
              )}

              {/* 🎨 PREDEFINED COLORS */}
              {ALLOWED_COLORS.map((color) => (
                <div
                  key={color}
                  title={COLOR_MAP[color]}
                  onMouseDown={(e) => e.preventDefault()} // ⛔ prevent blur
                  onClick={() => saveAndClose(color)}
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    backgroundColor: color,
                    cursor: "pointer",
                    border:
                      color === currentColor
                        ? "2px solid #000"
                        : "1px solid #ccc",
                  }}
                />
              ))}

              {/* ❌ CLEAR COLOUR */}
              <button
                type="button"
                title="Remove colour"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => saveAndClose(null)}
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  fontSize: 14,
                }}
              >
                ✕
              </button>
            </div>
          );
        },
      },
      {
        accessorKey: "email",
        header: "Email",
        enableEditing: true,
        enableColumnFilter: true,
        Edit: ({ cell, table }) => {
          const cellKey = `${cell.row.original.id}-${cell.column.id}`;
          const isSaving = savingCell === cellKey;
          const isSaved = !!savedCells[cellKey];
          const [error, setError] = React.useState("");
          const validateEmail = (email) => {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(email);
          };
          return (
            <div style={{ position: "relative" }}>
              <TextField
                fullWidth
                size="small"
                autoFocus
                defaultValue={cell.getValue()}
                disabled={isSaving}
                onBlur={async (e) => {
                  const newValue = e.target.value;
                  console.log(
                    "onBlur newValue:",
                    newValue,
                    "original value:",
                    cell.getValue(),
                  );
                  if (newValue !== cell.getValue()) {
                    if (!validateEmail(newValue)) {
                      toastr.error("Invalid email format");
                      return; // ❌ Stop here if invalid
                    }
                    setError("");
                    setSavingCell(cellKey);
                    await handleSaveCell(cell, newValue);
                    setSavingCell(null);
                    setSavedCells((prev) => ({ ...prev, [cellKey]: true }));
                  }
                  table.setEditingCell(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === "Escape")
                    e.currentTarget.blur();
                }}
              />

              {isSaving && (
                <CircularProgress
                  size={18}
                  sx={{
                    position: "absolute",
                    right: 6,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              )}

              {isSaved && !isSaving && (
                <CheckCircle
                  color="success"
                  fontSize="small"
                  sx={{
                    position: "absolute",
                    right: 6,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "phone",
        header: "Phone",
        enableEditing: true,
        enableColumnFilter: true,
        Cell: ({ row }) => {
          // show either phone or mobile value
          const { phone, mobile } = row.original;
          return phone || mobile || "";
        },
        Edit: ({ cell, table }) => {
          const cellKey = `${cell.row.original.id}-${cell.column.id}`;
          const isSaving = savingCell === cellKey;
          const isSaved = !!savedCells[cellKey];
          const [error, setError] = React.useState("");
          const validatePhone = (phone) => {
            const phoneRegex =
              /^(\+?\d[\d\s-]{7,})(\s*[,;]\s*\+?\d[\d\s-]{7,})*$/;
            return phoneRegex.test(phone.trim());
          };
          return (
            <div style={{ position: "relative" }}>
              <TextField
                fullWidth
                size="small"
                autoFocus
                defaultValue={cell.getValue()}
                disabled={isSaving}
                onBlur={async (e) => {
                  const newValue = e.target.value;
                  console.log(
                    "onBlur newValue:",
                    newValue,
                    "original value:",
                    cell.getValue(),
                  );
                  if (newValue !== cell.getValue()) {
                    if (!validatePhone(newValue)) {
                      toastr.error(
                        "Invalid phone format. Use +countrycode and digits only.",
                      );
                      return; // ❌ Don’t save or exit edit mode
                    }
                    setError("");
                    setSavingCell(cellKey);
                    await handleSaveCell(cell, newValue);
                    setSavingCell(null);
                    setSavedCells((prev) => ({ ...prev, [cellKey]: true }));
                  }
                  table.setEditingCell(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === "Escape")
                    e.currentTarget.blur();
                }}
              />

              {isSaving && (
                <CircularProgress
                  size={18}
                  sx={{
                    position: "absolute",
                    right: 6,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              )}

              {isSaved && !isSaving && (
                <CheckCircle
                  color="success"
                  fontSize="small"
                  sx={{
                    position: "absolute",
                    right: 6,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "designation",
        header: "Designation",
        enableEditing: false,
        enableColumnFilter: true,
      },
      {
        accessorKey: "company",
        header: "Current Company",
        enableEditing: false,
        enableColumnFilter: true,
      },
      {
        accessorKey: "prev_company",
        header: "Prev Company",
        enableEditing: true,
        enableColumnFilter: true,
        filterFn: "equals",
        Cell: ({ cell }) => cell.getValue(),
        Edit: ({ cell, table }) => {
          const cellKey = `${cell.row.original.id}-${cell.column.id}`;
          const isSaving = savingCell === cellKey;
          const isSaved = !!savedCells[cellKey];
          const [error, setError] = React.useState("");
          const [value, setValue] = React.useState(cell.getValue() ?? "");
          return (
            <div style={{ position: "relative" }}>
              <TextField
                fullWidth
                size="small"
                autoFocus
                value={value}
                disabled={isSaving}
                onChange={(e) => setValue(e.target.value)}
                onBlur={async () => {
                  if (value !== cell.getValue()) {
                    setError("");
                    setSavingCell(cellKey);
                    await FieldEdit(cell, value, "previous_companies");
                    setSavingCell(null);
                    setSavedCells((prev) => ({ ...prev, [cellKey]: true }));
                  }
                  table.setEditingCell(null);
                }}
                onKeyDown={async (e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (value !== cell.getValue()) {
                      setSavingCell(cellKey);
                      await FieldEdit(cell, value, "previous_companies");
                      setSavingCell(null);
                      setSavedCells((prev) => ({ ...prev, [cellKey]: true }));
                    }

                    table.setEditingCell(null);
                  }

                  if (e.key === "Escape") {
                    e.preventDefault();
                    table.setEditingCell(null); // cancel edit
                  }
                }}
              />

              {isSaving && (
                <CircularProgress
                  size={18}
                  sx={{
                    position: "absolute",
                    right: 6,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              )}

              {isSaved && !isSaving && (
                <CheckCircle
                  color="success"
                  fontSize="small"
                  sx={{
                    position: "absolute",
                    right: 6,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "major_industry",
        header: "Major Industry",
        enableEditing: false,
        enableColumnFilter: true,
      },
      {
        accessorKey: "major_status",
        header: "Status",
        enableEditing: true,
        enableColumnFilter: true,
        filterFn: "equals",
        Filter: ({ column }) => (
          <select
            className="form-select"
            value={column.getFilterValue() ?? ""}
            onChange={(e) =>
              column.setFilterValue(e.target.value ? e.target.value : null)
            }
            style={{ width: "100%", padding: "4px" }}
          >
            <option value="">All</option>
            {majorStatusOptions.map((option, i) => (
              <option key={i} value={option}>
                {option}
              </option>
            ))}
          </select>
        ),
        Cell: ({ cell }) => cell.getValue(),
        Edit: ({ cell, table }) => (
          <MajorStatusEdit
            key={cell.row.original.id}
            cell={cell}
            table={table}
          />
        ),
      },
      {
        accessorKey: "minor_status",
        header: "Candidate Status",
        enableEditing: true,
        enableColumnFilter: true,
        filterFn: "equals",
        Filter: ({ column }) => (
          <select
            className="form-select"
            value={column.getFilterValue() ?? ""}
            onChange={(e) =>
              column.setFilterValue(e.target.value ? e.target.value : null)
            }
            style={{ width: "100%", padding: "4px" }}
          >
            <option value="">All</option>
            {minorStatusOptions.map((option, i) => (
              <option key={i} value={option}>
                {option}
              </option>
            ))}
          </select>
        ),
        Cell: ({ cell }) => cell.getValue(),
        Edit: ({ cell, table }) => (
          <MinorStatusEdit
            key={cell.row.original.id}
            cell={cell}
            table={table}
          />
        ),
      },
      {
        accessorKey: "brief_comments",
        header: "Brief Comments",
        enableEditing: false,
        enableColumnFilter: true,
        Cell: ({ cell }) => {
          const value = cell.getValue();

          return (
            <div title={value || ""} style={{ overflowWrap: "break-word" }}>
              {value && value.length > 0
                ? value.slice(0, 100) + "..."
                : "No Notes Added"}
            </div>
          );
        },
      },
      {
        accessorKey: "Evaluation_comments",
        header: "Evaluation Comments",
        enableEditing: false,
        enableColumnFilter: true,
        Cell: ({ cell }) => {
          const value = cell.getValue();

          return (
            <div title={value || ""} style={{ overflowWrap: "break-word" }}>
              {value && value.length > 0
                ? value.slice(0, 100) + "..."
                : "No Notes Added"}
            </div>
          );
        },
      },
      {
        accessorKey: "country",
        header: "Country",
        enableEditing: true,
        enableColumnFilter: true,
        filterFn: "equals",
        Filter: ({ column }) => (
          <select
            className="form-select"
            value={column.getFilterValue() ?? ""}
            onChange={(e) => column.setFilterValue(e.target.value || undefined)}
            style={{ width: "100%", padding: "4px" }}
          >
            <option value="">All</option>
            {countryOptions.map((option, i) => (
              <option key={i} value={option}>
                {option}
              </option>
            ))}
          </select>
        ),
        Cell: ({ cell }) => cell.getValue(),
        Edit: ({ cell, table }) => (
          <CountryEdit key={cell.row.original.id} cell={cell} table={table} />
        ),
      },
      {
        accessorKey: "city",
        header: "City",
        enableEditing: true,
        enableColumnFilter: true,
        filterFn: "equals",
        Cell: ({ cell }) => cell.getValue(),
        Edit: ({ cell, table }) => {
          const cellKey = `${cell.row.original.id}-${cell.column.id}`;
          const isSaving = savingCell === cellKey;
          const isSaved = !!savedCells[cellKey];
          const [error, setError] = React.useState("");
          const [value, setValue] = React.useState(cell.getValue() ?? "");
          return (
            <div style={{ position: "relative" }}>
              <TextField
                fullWidth
                size="small"
                autoFocus
                value={value}
                disabled={isSaving}
                onChange={(e) => setValue(e.target.value)}
                onBlur={async () => {
                  if (value !== cell.getValue()) {
                    setError("");
                    setSavingCell(cellKey);
                    await FieldEdit(cell, value, "location");
                    setSavingCell(null);
                    setSavedCells((prev) => ({ ...prev, [cellKey]: true }));
                  }
                  table.setEditingCell(null);
                }}
                onKeyDown={async (e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (value !== cell.getValue()) {
                      setSavingCell(cellKey);
                      await FieldEdit(cell, value, "location");
                      setSavingCell(null);
                      setSavedCells((prev) => ({ ...prev, [cellKey]: true }));
                    }

                    table.setEditingCell(null);
                  }

                  if (e.key === "Escape") {
                    e.preventDefault();
                    table.setEditingCell(null); // cancel edit
                  }
                }}
              />

              {isSaving && (
                <CircularProgress
                  size={18}
                  sx={{
                    position: "absolute",
                    right: 6,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              )}

              {isSaved && !isSaving && (
                <CheckCircle
                  color="success"
                  fontSize="small"
                  sx={{
                    position: "absolute",
                    right: 6,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "experience_years",
        header: "Experience",
        enableEditing: false,
        enableColumnFilter: true,
      },
      {
        accessorKey: "current_ctc",
        header: "Current CTC",
        enableEditing: false,
        enableColumnFilter: true,
      },
      // Who they report to, and who reports to them. Read out of a
      // consultant's call notes, so the text is prose of any length - one
      // real cell runs to 2,060 characters. Rendered as a single clipped
      // line with the whole thing on hover: dropped in raw, one candidate's
      // notes set the height of every row in the grid.
      {
        accessorKey: "reporting_to_notes",
        header: "Reporting to",
        enableEditing: true,
        enableColumnFilter: true,
        size: 240,
        Cell: ({ cell }) => <ClippedNote value={cell.getValue()} />,
        Edit: ({ cell, table }) => (
          <NoteEdit
            cell={cell}
            table={table}
            onSave={saveReportingNote}
            saving={savingCell === `${cell.row.original.id}-${cell.column.id}`}
          />
        ),
      },
      {
        accessorKey: "team_structure_notes",
        header: "Team structure",
        enableEditing: true,
        enableColumnFilter: true,
        size: 240,
        Cell: ({ cell }) => <ClippedNote value={cell.getValue()} />,
        Edit: ({ cell, table }) => (
          <NoteEdit
            cell={cell}
            table={table}
            onSave={saveReportingNote}
            saving={savingCell === `${cell.row.original.id}-${cell.column.id}`}
          />
        ),
      },
      // The viewer's own note from AI Sourcing Analysis - the same row the
      // panel autosaves - with others' notes on hover.
      {
        accessorKey: "consultant_notes",
        header: "Consultant Notes",
        enableEditing: true,
        enableColumnFilter: true,
        size: 240,
        Cell: ({ row }) => <ConsultantNoteCell row={row} />,
        Edit: ({ cell, table }) => (
          <NoteEdit
            cell={cell}
            table={table}
            onSave={saveConsultantNote}
            saving={savingCell === `${cell.row.original.id}-${cell.column.id}`}
          />
        ),
      },
      {
        accessorKey: "added_by",
        header: "Added By",
        enableEditing: false,
        enableColumnFilter: true,
        filterFn: "equals",
        Filter: ({ column }) => (
          <select
            className="form-select"
            value={column.getFilterValue() ?? ""}
            onChange={(e) =>
              column.setFilterValue(e.target.value ? e.target.value : null)
            }
            style={{ width: "100%", padding: "4px" }}
          >
            <option value="">All</option>
            {addedByOptions.map((option, i) => (
              <option key={i} value={option}>
                {option}
              </option>
            ))}
          </select>
        ),
      },
      {
        accessorKey: "preferred_location",
        header: "Preferred Location",
        enableEditing: true,
        enableColumnFilter: true,
        filterFn: "equals",
        Cell: ({ cell }) => cell.getValue(),
        Edit: ({ cell, table }) => {
          const cellKey = `${cell.row.original.id}-${cell.column.id}`;
          const isSaving = savingCell === cellKey;
          const isSaved = !!savedCells[cellKey];
          const [error, setError] = React.useState("");
          const [value, setValue] = React.useState(cell.getValue() ?? "");
          return (
            <div style={{ position: "relative" }}>
              <TextField
                fullWidth
                size="small"
                autoFocus
                value={value}
                disabled={isSaving}
                onChange={(e) => setValue(e.target.value)}
                onBlur={async () => {
                  if (value !== cell.getValue()) {
                    setError("");
                    setSavingCell(cellKey);
                    await FieldEdit(cell, value, "preferred_location");
                    setSavingCell(null);
                    setSavedCells((prev) => ({ ...prev, [cellKey]: true }));
                  }
                  table.setEditingCell(null);
                }}
                onKeyDown={async (e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (value !== cell.getValue()) {
                      setSavingCell(cellKey);
                      await FieldEdit(cell, value, "preferred_location");
                      setSavingCell(null);
                      setSavedCells((prev) => ({ ...prev, [cellKey]: true }));
                    }

                    table.setEditingCell(null);
                  }

                  if (e.key === "Escape") {
                    e.preventDefault();
                    table.setEditingCell(null); // cancel edit
                  }
                }}
              />

              {isSaving && (
                <CircularProgress
                  size={18}
                  sx={{
                    position: "absolute",
                    right: 6,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              )}

              {isSaved && !isSaving && (
                <CheckCircle
                  color="success"
                  fontSize="small"
                  sx={{
                    position: "absolute",
                    right: 6,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "background",
        header: "Education",
        enableEditing: true,
        enableColumnFilter: true,
        filterFn: "equals",
        Cell: ({ cell }) => cell.getValue(),
        Edit: ({ cell, table }) => {
          const cellKey = `${cell.row.original.id}-${cell.column.id}`;
          const isSaving = savingCell === cellKey;
          const isSaved = !!savedCells[cellKey];
          const [error, setError] = React.useState("");
          const [value, setValue] = React.useState(cell.getValue() ?? "");
          return (
            <div style={{ position: "relative" }}>
              <TextField
                fullWidth
                size="small"
                autoFocus
                value={value}
                disabled={isSaving}
                onChange={(e) => setValue(e.target.value)}
                onBlur={async () => {
                  if (value !== cell.getValue()) {
                    setError("");
                    setSavingCell(cellKey);
                    await FieldEdit(cell, value, "background");
                    setSavingCell(null);
                    setSavedCells((prev) => ({ ...prev, [cellKey]: true }));
                  }
                  table.setEditingCell(null);
                }}
                onKeyDown={async (e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (value !== cell.getValue()) {
                      setSavingCell(cellKey);
                      await FieldEdit(cell, value, "background");
                      setSavingCell(null);
                      setSavedCells((prev) => ({ ...prev, [cellKey]: true }));
                    }

                    table.setEditingCell(null);
                  }

                  if (e.key === "Escape") {
                    e.preventDefault();
                    table.setEditingCell(null); // cancel edit
                  }
                }}
              />

              {isSaving && (
                <CircularProgress
                  size={18}
                  sx={{
                    position: "absolute",
                    right: 6,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              )}

              {isSaved && !isSaving && (
                <CheckCircle
                  color="success"
                  fontSize="small"
                  sx={{
                    position: "absolute",
                    right: 6,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "skills",
        header: "Skills",
        enableEditing: true,
        enableColumnFilter: true,
        filterFn: "equals",
        Cell: ({ cell }) => cell.getValue(),
        Edit: ({ cell, table }) => {
          const cellKey = `${cell.row.original.id}-${cell.column.id}`;
          const isSaving = savingCell === cellKey;
          const isSaved = !!savedCells[cellKey];
          const [error, setError] = React.useState("");
          const [value, setValue] = React.useState(cell.getValue() ?? "");
          return (
            <div style={{ position: "relative" }}>
              <TextField
                fullWidth
                size="small"
                autoFocus
                value={value}
                disabled={isSaving}
                onChange={(e) => setValue(e.target.value)}
                onBlur={async () => {
                  if (value !== cell.getValue()) {
                    setError("");
                    setSavingCell(cellKey);
                    await FieldEdit(cell, value, "skills");
                    setSavingCell(null);
                    setSavedCells((prev) => ({ ...prev, [cellKey]: true }));
                  }
                  table.setEditingCell(null);
                }}
                onKeyDown={async (e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (value !== cell.getValue()) {
                      setSavingCell(cellKey);
                      await FieldEdit(cell, value, "skills");
                      setSavingCell(null);
                      setSavedCells((prev) => ({ ...prev, [cellKey]: true }));
                    }

                    table.setEditingCell(null);
                  }

                  if (e.key === "Escape") {
                    e.preventDefault();
                    table.setEditingCell(null); // cancel edit
                  }
                }}
              />

              {isSaving && (
                <CircularProgress
                  size={18}
                  sx={{
                    position: "absolute",
                    right: 6,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              )}

              {isSaved && !isSaving && (
                <CheckCircle
                  color="success"
                  fontSize="small"
                  sx={{
                    position: "absolute",
                    right: 6,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "potential",
        header: "Potential",
        enableEditing: true,
        enableColumnFilter: true,
        filterFn: "equals",
        Cell: ({ cell }) => cell.getValue(),
        Edit: ({ cell, table }) => {
          const cellKey = `${cell.row.original.id}-${cell.column.id}`;
          const isSaving = savingCell === cellKey;
          const isSaved = !!savedCells[cellKey];
          const [error, setError] = React.useState("");
          const [value, setValue] = React.useState(cell.getValue() ?? "");
          return (
            <div style={{ position: "relative" }}>
              <TextField
                fullWidth
                size="small"
                autoFocus
                value={value}
                disabled={isSaving}
                onChange={(e) => setValue(e.target.value)}
                onBlur={async () => {
                  if (value !== cell.getValue()) {
                    setError("");
                    setSavingCell(cellKey);
                    await FieldEdit(cell, value, "potential");
                    setSavingCell(null);
                    setSavedCells((prev) => ({ ...prev, [cellKey]: true }));
                  }
                  table.setEditingCell(null);
                }}
                onKeyDown={async (e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (value !== cell.getValue()) {
                      setSavingCell(cellKey);
                      await FieldEdit(cell, value, "potential");
                      setSavingCell(null);
                      setSavedCells((prev) => ({ ...prev, [cellKey]: true }));
                    }

                    table.setEditingCell(null);
                  }

                  if (e.key === "Escape") {
                    e.preventDefault();
                    table.setEditingCell(null); // cancel edit
                  }
                }}
              />

              {isSaving && (
                <CircularProgress
                  size={18}
                  sx={{
                    position: "absolute",
                    right: 6,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              )}

              {isSaved && !isSaving && (
                <CheckCircle
                  color="success"
                  fontSize="small"
                  sx={{
                    position: "absolute",
                    right: 6,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "motivation_to_move",
        header: "Motivation to Move",
        enableEditing: true,
        enableColumnFilter: true,
        filterFn: "equals",
        Cell: ({ cell }) => cell.getValue(),
        Edit: ({ cell, table }) => {
          const cellKey = `${cell.row.original.id}-${cell.column.id}`;
          const isSaving = savingCell === cellKey;
          const isSaved = !!savedCells[cellKey];
          const [error, setError] = React.useState("");
          const [value, setValue] = React.useState(cell.getValue() ?? "");
          return (
            <div style={{ position: "relative" }}>
              <TextField
                fullWidth
                size="small"
                autoFocus
                value={value}
                disabled={isSaving}
                onChange={(e) => setValue(e.target.value)}
                onBlur={async () => {
                  if (value !== cell.getValue()) {
                    setError("");
                    setSavingCell(cellKey);
                    await FieldEdit(cell, value, "motivation_to_move");
                    setSavingCell(null);
                    setSavedCells((prev) => ({ ...prev, [cellKey]: true }));
                  }
                  table.setEditingCell(null);
                }}
                onKeyDown={async (e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (value !== cell.getValue()) {
                      setSavingCell(cellKey);
                      await FieldEdit(cell, value, "motivation_to_move");
                      setSavingCell(null);
                      setSavedCells((prev) => ({ ...prev, [cellKey]: true }));
                    }

                    table.setEditingCell(null);
                  }

                  if (e.key === "Escape") {
                    e.preventDefault();
                    table.setEditingCell(null); // cancel edit
                  }
                }}
              />

              {isSaving && (
                <CircularProgress
                  size={18}
                  sx={{
                    position: "absolute",
                    right: 6,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              )}

              {isSaved && !isSaving && (
                <CheckCircle
                  color="success"
                  fontSize="small"
                  sx={{
                    position: "absolute",
                    right: 6,
                    top: "50%",
                    transform: "translateY(-50%)",
                  }}
                />
              )}
            </div>
          );
        },
      },
      {
        accessorKey: "created",
        header: "Created At",
        enableEditing: false,
        enableColumnFilter: true,
      },
      {
        id: "ai_actions",
        header: "AI Actions",
        enableEditing: false,
        enableColumnFilter: false,
        enableSorting: false,
        enableColumnActions: false,
        // The design's width and spacing for four buttons; at 150 the video icon was clipped.
        size: 180,
        Cell: ({ row }) => (
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <AiActionButton
              title="AI Source Analysis"
              onClick={() => handleAiAction("source", row.original)}
            >
              <IconBot />
            </AiActionButton>
            <AiActionButton
              title="Draft Outreach"
              onClick={() => handleAiAction("outreach", row.original)}
              sent={sentMap[row.original.candidate_id]?.outreach}
            >
              <IconSend />
            </AiActionButton>
            <AiActionButton
              title="Draft Follow-up"
              onClick={() => handleAiAction("followup", row.original)}
              sent={sentMap[row.original.candidate_id]?.followup}
            >
              <IconFollowUp />
            </AiActionButton>
            <VideoActionButton
              onClick={() => setVideoFor(row.original)}
              status={videoMap[row.original.candidate_id]}
            />
          </div>
        ),
      },
    ],
    [
      data,
      selectedRows,
      savingCell,
      savedCells,
      majorStatusOptions,
      minorStatusOptions,
      countryOptions,
      addedByOptions,
      sentMap,
      videoMap,
    ],
  );

  function MajorStatusEdit({ cell, table }) {
    const cellKey = `${cell.row.original.id}-${cell.column.id}`;
    const isSaving = savingCell === cellKey;
    const isSaved = !!savedCells[cellKey];
    const [options, setOptions] = useState([]);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [error, setError] = useState("");

    // 🔹 Fetch dropdown options from API
    useEffect(() => {
      let mounted = true;
      const fetchMajorStatuses = async () => {
        try {
          setLoadingOptions(true);
          const data = await getOptionsCached(
            `${API_BASE_URL}/workstation/get_master_status_list`,
            token,
          );
          if (mounted && Array.isArray(data.master_status_list)) {
            setOptions(data.master_status_list);
          } else {
            setOptions([]);
          }
        } catch (err) {
          console.error("Failed to load major statuses:", err);
          setError("Failed to load options");
        } finally {
          setLoadingOptions(false);
        }
      };

      fetchMajorStatuses();
      return () => {
        mounted = false;
      };
    }, []);

    return (
      <div style={{ position: "relative" }}>
        <select
          className="form-select form-select-sm"
          defaultValue={cell.getValue() || ""}
          disabled={isSaving || loadingOptions}
          error={!!error}
          helperText={error || ""}
          onChange={async (e) => {
            const newValue = e.target.value;
            if (newValue !== cell.getValue()) {
              majorChangedRef.current[cell.row.original.id] = true;
              // await handleSaveCell(cell, newValue);
              await temp_store_cell_value(cell, newValue);
              try {
                const res = await fetch(
                  `${API_BASE_URL}/workstation/get_minor_status_list/${newValue}`,
                  { headers: { Authorization: `Bearer ${token}` } },
                );
                const data = await res.json();
                console.log(data);
                if (
                  Array.isArray(data.minor_status_list) &&
                  data.minor_status_list.length > 0
                ) {
                  // const firstMinor = data.minor_status_list[0].name;
                  const firstMinor = "Select Status";
                  const firstMinorId = data.minor_status_list[0].id;
                  setData((prev) =>
                    prev.map((r) =>
                      r.id === cell.row.original.id
                        ? { ...r, minor_status: firstMinor } // auto-set first minor status
                        : r,
                    ),
                  );

                  const minorCellFake = {
                    row: { original: { id: cell.row.original.id } },
                    column: { id: "minor_status" },
                  };
                  // await handleSaveCell(minorCellFake, firstMinor);
                  await temp_store_cell_value(minorCellFake, firstMinor);
                  // setSelectedMinorStatus(firstMinorId);

                  // if (statusRequiringModalOpen.includes(firstMinorId)) {
                  setSelectedCandidate(cell.row.original);
                  // setGlobalLoading(true);
                  // setShowEditStatusModal(true);
                  // }
                } else {
                  // Clear minor status if no options
                  setData((prev) =>
                    prev.map((r) =>
                      r.id === cell.row.original.id
                        ? { ...r, minor_status: "" }
                        : r,
                    ),
                  );
                }
              } catch (err) {
                console.error(
                  "Failed to update minor status automatically",
                  err,
                );
              } finally {
                table.setEditingCell(null);
              }
            }
          }}
        >
          <option value={""}>Select Status</option>
          {loadingOptions ? (
            <MenuItem disabled>Loading...</MenuItem>
          ) : (
            options.map((opt, index) => (
              <option key={index} value={opt.name}>
                {opt.name}
              </option>
            ))
          )}
        </select>

        {isSaving && (
          <CircularProgress
            size={18}
            sx={{
              position: "absolute",
              right: 6,
              top: "50%",
              transform: "translateY(-50%)",
            }}
          />
        )}

        {isSaved && !isSaving && !error && (
          <CheckCircle
            color="success"
            fontSize="small"
            sx={{
              position: "absolute",
              right: 6,
              top: "50%",
              transform: "translateY(-50%)",
            }}
          />
        )}
      </div>
    );
  }

  function MinorStatusEdit({ cell, table }) {
    const [options, setOptions] = useState([]);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const selectRef = useRef(null);

    // local temporary value so we can "unselect" visually on open
    const [tempValue, setTempValue] = useState(cell.getValue() || "");

    useEffect(() => {
      setTempValue(cell.getValue() || ""); // keep in sync if row data changes externally
    }, [cell.getValue()]);

    useEffect(() => {
      (async () => {
        setLoadingOptions(true);
        try {
          const data = await getOptionsCached(
            `${API_BASE_URL}/workstation/get_minor_status_list/${cell.row.original.major_status}`,
            token,
          );
          setOptions(data.minor_status_list || []);
        } catch (err) {
          console.error("Failed to load minor statuses:", err);
        } finally {
          setLoadingOptions(false);
        }
      })();
    }, [cell.row.original.major_status]);

    // When user opens the select, show it as unselected (but keep options)
    const handleMouseDown = () => {
      // clear temporary value so already-selected option becomes selectable
      setTempValue("");
    };

    // User selected a value (could be same or different)
    const handleChange = async (e) => {
      const newValue = e.target.value;
      const major_status = cell.row.original.major_status;
      setTempValue(newValue); // reflect selection immediately
      // Save/update as you already do
      await temp_store_cell_value(cell, newValue);
      const minor_detail = await get_minor_status_details(
        newValue,
        major_status,
      );
      const firstMinorId = minor_detail.candidate_status_list?.[0]?.id;
      if (firstMinorId) setSelectedMinorStatus(firstMinorId);
      setSelectedCandidate(cell.row.original);
      setGlobalLoading(true);
      setShowEditStatusModal(true);
      table.setEditingCell(null);
    };

    // When dropdown closes. If user didn't pick anything (tempValue === ""),
    // refresh table and restore tempValue to actual cell value.
    const handleBlur = async (e) => {
      if (!tempValue) {
        // user opened and didn't select — refresh data
        if (typeof table.options.meta?.fetchData === "function") {
          table.options.meta.fetchData();
        } else if (typeof fetchData === "function") {
          fetchData(); // fallback if you have a local fetchData
        }
        // restore to real value from cell (no change)
        setTempValue(cell.getValue() || "");
      } else {
        // keep tempValue in sync with cell value (in case saved by API)
        setTempValue(cell.getValue() || tempValue);
      }
    };

    return (
      <select
        ref={selectRef}
        className="form-select form-select-sm minor_status"
        value={tempValue}
        disabled={loadingOptions}
        onMouseDown={handleMouseDown}
        onChange={handleChange}
        onBlur={handleBlur}
      >
        <option value="">Select Status</option>
        {loadingOptions ? (
          <option disabled>Loading...</option>
        ) : options.length > 0 ? (
          options.map((opt) => (
            <option key={opt.id} value={opt.name}>
              {opt.name}
            </option>
          ))
        ) : (
          <option disabled>No minor statuses</option>
        )}
      </select>
    );
  }

  function CountryEdit({ cell, table }) {
    const [options, setOptions] = useState([]);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const selectRef = useRef(null);

    // local temporary value so we can "unselect" visually on open
    const [tempValue, setTempValue] = useState(cell.getValue() || "");

    useEffect(() => {
      setTempValue(cell.getValue() || ""); // keep in sync if row data changes externally
    }, [cell.getValue()]);

    useEffect(() => {
      (async () => {
        setLoadingOptions(true);
        try {
          const data = await getOptionsCached(
            `${API_BASE_URL}/common_controller/country_region_map`,
            token,
          );
          setOptions(data || []);
        } catch (err) {
          console.error("Failed to load countries:", err);
        } finally {
          setLoadingOptions(false);
        }
      })();
    }, []);

    // When user opens the select, show it as unselected (but keep options)
    const handleMouseDown = () => {
      // clear temporary value so already-selected option becomes selectable
      setTempValue("");
    };

    // User selected a value (could be same or different)
    const handleChange = async (e) => {
      const newValue = e.target.value;
      setTempValue(newValue); // reflect selection immediately
      // Save/update as you already do
      await temp_store_cell_value(cell, newValue);
      const selected_country_id = options.find(
        (c) => c.country === newValue,
      ).country_id;
      await axios
        .put(
          `${API_BASE_URL}/common_controller/update_fields`,
          {
            id: cell.row.original.candidate_id,
            fields: { country_id: selected_country_id },
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        )
        .then((res) => toastr.success("Country Updated Successfully"))
        .catch((err) => console.error(err));
      table.setEditingCell(null);
    };

    // When dropdown closes. If user didn't pick anything (tempValue === ""),
    // refresh table and restore tempValue to actual cell value.
    const handleBlur = (e) => {
      if (!tempValue) {
        // user opened and didn't select — refresh data
        if (typeof table.options.meta?.fetchData === "function") {
          table.options.meta.fetchData();
        } else if (typeof fetchData === "function") {
          fetchData(); // fallback if you have a local fetchData
        }
        // restore to real value from cell (no change)
        setTempValue(cell.getValue() || "");
        table.setEditingCell(null);
      } else {
        // keep tempValue in sync with cell value (in case saved by API)
        setTempValue(cell.getValue() || tempValue);
        table.setEditingCell(null);
      }
    };

    return (
      <select
        ref={selectRef}
        className="form-select form-select-sm"
        value={tempValue}
        disabled={loadingOptions}
        onMouseDown={handleMouseDown}
        onChange={handleChange}
        onBlur={handleBlur}
      >
        <option value="">Select Country</option>
        {loadingOptions ? (
          <option disabled>Loading...</option>
        ) : options.length > 0 ? (
          options.map((opt) => (
            <option key={opt.country_id} value={opt.country}>
              {opt.country}
            </option>
          ))
        ) : (
          <option disabled>No countries</option>
        )}
      </select>
    );
  }

  /**
   * Save a reporting or team note.
   *
   * Separate from FieldEdit because that one posts to update_fields, which
   * writes the candidates table. These two columns live on
   * candidate_reporting_info and are upserted by their own endpoint.
   *
   * Sends only the field being edited. Sending both would blank whichever
   * column the user did not touch.
   */
  const saveReportingNote = async (cell, newValue) => {
    const rowId = cell.row.original.id;
    const columnId = cell.column.id;
    const candidate_id = cell.row.original.candidate_id;
    const cellKey = `${rowId}-${columnId}`;

    setSavingCell(cellKey);
    try {
      await axios.put(
        `${API_BASE_URL}/candidates/save_reporting_info`,
        { candidate_id, [columnId]: newValue },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setData((prev) =>
        prev.map((r) => (r.id === rowId ? { ...r, [columnId]: newValue } : r)),
      );
      setSavedCells((s) => ({ ...s, [cellKey]: true }));
      setTimeout(() => {
        setSavedCells((s) => {
          const copy = { ...s };
          delete copy[cellKey];
          return copy;
        });
      }, 1500);
    } catch (err) {
      console.error("save_reporting_info failed", err);
      toastr.error("Could not save that note.");
    } finally {
      setSavingCell(null);
    }
  };

  /**
   * Save the viewer's Consultant Note - the same note AI Sourcing Analysis
   * autosaves, through the same endpoint, so the grid and the panel always
   * show one text.
   */
  const saveConsultantNote = async (cell, newValue) => {
    const rowId = cell.row.original.id;
    const columnId = cell.column.id;
    const cellKey = `${rowId}-${columnId}`;

    setSavingCell(cellKey);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/workstation/analysis_note`,
        {
          role_id: id,
          candidate_id: cell.row.original.candidate_id,
          body: newValue,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setData((prev) =>
        prev.map((r) =>
          r.id === rowId
            ? {
                ...r,
                consultant_notes: newValue,
                consultant_notes_saved_at: res.data?.saved_at || null,
              }
            : r,
        ),
      );
      setSavedCells((s) => ({ ...s, [cellKey]: true }));
      setTimeout(() => {
        setSavedCells((s) => {
          const copy = { ...s };
          delete copy[cellKey];
          return copy;
        });
      }, 1500);
    } catch (err) {
      console.error("analysis_note failed", err);
      toastr.error(err.response?.data?.error || "Could not save that note.");
    } finally {
      setSavingCell(null);
    }
  };

  const FieldEdit = async (cell, newValue, field) => {
    const rowId = cell.row.original.id;
    const columnId = cell.column.id;
    const candidate_id = cell.row.original.candidate_id;
    const cellKey = `${rowId}-${columnId}`;
    setSavingCell(cellKey);
    try {
      await axios.put(
        `${API_BASE_URL}/common_controller/update_fields`,
        { id: candidate_id, fields: { [field]: newValue } },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // optimistic update
      setData((prev) =>
        prev.map((r) => (r.id === rowId ? { ...r, [columnId]: newValue } : r)),
      );
      // show check
      setSavedCells((s) => ({ ...s, [cellKey]: true }));
      setTimeout(() => {
        setSavedCells((s) => {
          const copy = { ...s };
          delete copy[cellKey];
          return copy;
        });
      }, 1500);
    } catch (err) {
      setData((prev) =>
        prev.map((r) =>
          r.id === rowId
            ? {
                ...r,
                [columnId]: newValue,
              }
            : r,
        ),
      );
      // show check
      setSavedCells((s) => ({ ...s, [cellKey]: true }));
      setTimeout(() => {
        setSavedCells((s) => {
          const copy = { ...s };
          delete copy[cellKey];
          return copy;
        });
      }, 1500);
      console.error("Update failed", err);
    } finally {
      setSavingCell(null);
    }
  };

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/workstation/get_candidate_list/${id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      console.log(res.data.candidate_list);
      setData(res.data.candidate_list || []);
      console.log(res.data.role_details);

      setRoleDetails(res.data.role_details || {});
    } catch (err) {
      console.error(err);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [pagination, sorting, columnFilters, id, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaveCell = async (cell, newValue) => {
    const rowId = cell.row.original.id;
    const columnId = cell.column.id;
    const cellKey = `${rowId}-${columnId}`;
    setSavingCell(cellKey);
    try {
      await axios.put(
        `${API_BASE_URL}/workstation/update_candidate_info/${rowId}`,
        { [columnId === "name" ? "firstname" : columnId]: newValue },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      // optimistic update
      setData((prev) =>
        prev.map((r) => (r.id === rowId ? { ...r, [columnId]: newValue } : r)),
      );
      // show check
      setSavedCells((s) => ({ ...s, [cellKey]: true }));
      setTimeout(() => {
        setSavedCells((s) => {
          const copy = { ...s };
          delete copy[cellKey];
          return copy;
        });
      }, 1500);
    } catch (err) {
      setData((prev) =>
        prev.map((r) =>
          r.id === rowId
            ? {
                ...r,
                [columnId]: newValue,
              }
            : r,
        ),
      );
      // show check
      setSavedCells((s) => ({ ...s, [cellKey]: true }));
      setTimeout(() => {
        setSavedCells((s) => {
          const copy = { ...s };
          delete copy[cellKey];
          return copy;
        });
      }, 1500);
      console.error("Update failed", err);
    } finally {
      setSavingCell(null);
    }
  };

  const temp_store_cell_value = async (cell, newValue) => {
    const rowId = cell.row.original.id;
    const columnId = cell.column.id;
    const cellKey = `${rowId}-${columnId}`;
    // optimistic update
    setData((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, [columnId]: newValue } : r)),
    );
    // show check
    setSavedCells((s) => ({ ...s, [cellKey]: true }));
    setTimeout(() => {
      setSavedCells((s) => {
        const copy = { ...s };
        delete copy[cellKey];
        return copy;
      });
    }, 1500);
  };

  const clickTimeout = useRef(null);

  const table = useMaterialReactTable({
    columns,
    data,

    // =========================
    // Row Actions
    // =========================
    enableRowActions: true,
    positionActionsColumn: "last",

    renderRowActions: ({ row }) => (
      <Tooltip title="Delete">
        <IconButton color="error" onClick={() => handleDelete(row.original.id)}>
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    ),

    // =========================
    // Pinning / Sticky / Resize
    // =========================
    enableColumnPinning: true,
    enableStickyHeader: true,
    enableColumnResizing: true,

    columnResizeMode: "onEnd",

    // This is useful when using pinned columns
    // layoutMode: "grid-no-grow",

    // =========================
    // Column Visibility
    // =========================
    // =========================
    // Column Visibility
    // =========================
    enableHiding: true,

    onColumnVisibilityChange: setColumnVisibility,
    onColumnSizingChange: setColumnSizing,

    // =========================
    // Column Pinning
    // =========================
    onColumnPinningChange: handleColumnPinningChange,

    // =========================
    // Filters
    // =========================
    enableColumnFilters: true,
    enableFilters: true,
    enableGlobalFilter: true,

    // =========================
    // Editing
    // =========================
    enableEditing: true,
    editDisplayMode: "cell",

    autoResetPageIndex: false,

    // =========================
    // Container
    // =========================
    muiTableContainerProps: {
      sx: {
        maxHeight: "85vh",
        overflowX: "auto",
      },
    },

    // =========================
    // Body Cells
    // =========================
    muiTableBodyCellProps: ({ cell, table }) => ({
      onClick: () => {
        if (!cell.column.columnDef.enableEditing) return;

        // The dropdown itself comes from setEditingCell: Candidate Status used to
        // get it only from the table's own double click.
        const open = () => {
          if (cell.column.id === "minor_status") {
            const rowData = cell.row.original;

            setSelectedCandidate(rowData);
            fetchCandidateStatus(rowData.id);
          }
          table.setEditingCell({
            id: cell.id,
            rowId: cell.row.id,
            columnId: cell.column.id,
          });
        };

        if (SINGLE_CLICK_COLUMNS.includes(cell.column.id)) {
          // A click inside the open dropdown must not reopen it.
          if (table.getState().editingCell?.id !== cell.id) open();
          return;
        }

        if (clickTimeout.current) {
          clearTimeout(clickTimeout.current);
          clickTimeout.current = null;
          open();
        } else {
          clickTimeout.current = setTimeout(() => {
            clickTimeout.current = null;
          }, 250);
        }
      },

      title: !cell.column.columnDef.enableEditing
        ? ""
        : SINGLE_CLICK_COLUMNS.includes(cell.column.id)
          ? "Click to edit"
          : "Double click to edit",

      sx: {
        cursor: cell.column.columnDef.enableEditing ? "pointer" : "default",
        whiteSpace: wrapText ? "normal" : "nowrap",
        overflow: wrapText ? "visible" : "hidden",
        textOverflow: wrapText ? "clip" : "ellipsis",
        wordBreak: wrapText ? "break-word" : "normal",
      },
    }),

    // =========================
    // Meta
    // =========================
    meta: {
      updateData: (rowIndex, columnId, value) => {
        setData((prev) =>
          prev.map((row, index) =>
            index === rowIndex ? { ...row, [columnId]: value } : row,
          ),
        );
      },

      saveColour: async (FilterId, colour) => {
        try {
          await axios.post(
            `${API_BASE_URL}/candidates/updateColour`,
            {
              id: FilterId,
              colour,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );
        } catch (err) {
          console.error("Failed to update colour", err);
        }
      },
    },

    // =========================
    // Initial State
    // =========================
    initialState: {
      showColumnFilters: true,

      pagination: {
        pageIndex: 0,
        pageSize: 50,
      },
    },

    // =========================
    // Controlled State
    // =========================
    state: {
      sorting,
      columnFilters,
      isLoading: loading,

      columnVisibility,
      columnSizing,

      // Controlled pinning state
      columnPinning,
    },

    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,

    getRowId: (row) => row.id,
  });

  const handleCreatePPT = async () => {
    if (selectedRows.length === 0) {
      alert("Please select at least one candidate before creating PPT.");
      return;
    }
    try {
      setLoading(true);
      const response = await axios.post(
        `${API_BASE_URL}/workstation/create_ppt`,
        {
          role_id: id,
          candidate_ids: selectedRows,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const message = response.data.message;
      if (message == "Issue Candidate") {
        const issue_candidate = response.data.issueCandidate;
        const issue_candidate_name = issue_candidate
          .map(
            (item) =>
              `${item.firstname} ${item.lastname} - ${item.issues.join(", ")}`,
          )
          .join(", ");
        toastr.error(`Selected candidates has issues: ${issue_candidate_name}`);
        return;
      }
      const filename = response.data.filename;
      const fileUrl = `${APP_PINE_BASE_URL}/mediaPPT/${filename}`;

      // Create hidden <a> tag and trigger download
      // window.open(fileUrl, "_blank");
      const link = document.createElement("a");
      link.href = fileUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error creating PPT:", error);
      alert("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  // const handleCreatePPTReact = async () => {
  //   if (selectedRows.length === 0) {
  //     toastr.error("Please select at least one candidate before creating PPT.");
  //     return;
  //   }

  //   try {
  //     setLoading(true);

  //     // Convert selected IDs to comma-separated string
  //     const candidateIds = selectedRows.join(",");

  //     // 👀 Debug logs
  //     console.log("Selected Rows (array):", selectedRows);
  //     console.log("Candidate IDs (comma-separated):", candidateIds);
  //     console.log("Final API URL:", `${API_BASE_URL}/ppt/demo-ppt/${candidateIds}`);

  //     const response = await axios.get(
  //       `${API_BASE_URL}/ppt/demo-ppt/${candidateIds}`,
  //       {
  //         headers: { Authorization: `Bearer ${token}` },
  //         responseType: "blob", // important for file download
  //       }
  //     );

  //     // Trigger download
  //     const url = window.URL.createObjectURL(new Blob([response.data]));
  //     const link = document.createElement("a");
  //     link.href = url;
  //     link.setAttribute(
  //       "download",
  //       `candidates_${candidateIds.replace(/,/g, "_")}_profile.pptx`
  //     );
  //     document.body.appendChild(link);
  //     link.click();
  //     link.remove();

  //   } catch (error) {
  //     console.error("Error creating PPT:", error);
  //     alert("Something went wrong while creating PPT.");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const generatePPT = async (isMasked) => {
    if (selectedRows.length === 0) {
      toastr.error("Please select at least one candidate.");
      return;
    }

    setShowPptModal(false);
    setIsRefreshing(true);

    // ── Step 1: SignalHire update for all selected candidates ──
    let failedCandidates = [];

    try {
      const response = await axios.post(
        `${API_BASE_URL}/candidates/signalhire-batch-update`,
        { candidate_ids: selectedRows },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      failedCandidates = response.data.failed || [];
    } catch (err) {
      console.error("SignalHire batch update error:", err);
      // non-fatal — still proceed to PPT
      failedCandidates = selectedRows.map((id) => ({
        candidateId: id,
        message: "Update request failed",
      }));
    }

    // ── Step 2: Warn about any failures ──
    if (failedCandidates.length) {
      const names = failedCandidates
        .map((f) => `#${f.candidateId}: ${f.message}`)
        .join("\n");
      toastr.warning(
        `Some candidates could not be refreshed — PPT will use existing data:\n${names}`,
        "Partial update",
        { timeOut: 8000 },
      );
    }

    setIsRefreshing(false);

    toastr.success(
      `PPT is being generated for ${selectedRows.length} candidates. Soon will be in your downloads.`,
    );

    // ── Step 3: Generate PPT as before ──
    try {
      const candidateIds = selectedRows.join(",");
      const url = `${API_BASE_URL}/ppt/demo-ppt/${candidateIds}/${isMasked}?role_id=${id}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      const fileName = isMasked
        ? `candidates_${candidateIds}_MASKED.pptx`
        : `candidates_${candidateIds}_UNMASKED.pptx`;

      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("PPT error:", error);
      toastr.error("Failed to generate PPT.");
    }
  };

  const handleBack = useSmartBack("/workstation/list");

  const handleUpdateLushaFlag = async () => {
    setIsLushaLoading(true); // loader inside the contact modal
    try {
      const payload = {
        candidate_id: lushaCandidateId,
      };

      const response = await axios.post(
        `${API_BASE_URL}/candidates/update_candidate_contacts`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      console.log("response:", response);

      if (response.status === 200) {
        const { message, data: responseData } = response.data; // rename to avoid shadowing component `data`

        const lusha_status_id = responseData.lusha_status_id;
        // Update lusha state
        setLushaPhone(responseData.phone);
        setLushaEmail(responseData.email);
        setLushaFlag(Number(lusha_status_id));

        // ✅ Also patch the main `data` object so sidebar reflects immediately
        // without waiting for fetchOptions to complete
        if (responseData.phone || responseData.email) {
          setData((prev) => ({
            ...prev,
            contacts: responseData.phone || prev.contacts,
            email: responseData.email || prev.email,
          }));
        }

        // Keep the modal open only if there are contacts to review
        setShowPhoneModal(Number(lusha_status_id) === 4 && !!responseData.phone);

        toastr.success(message || "Contacts updated successfully.");
      }

      fetchData(true); // refresh table without showing the table loader
    } catch (error) {
      console.error("❌ Error updating Lusha flag:", error);
      toastr.error(
        error.response?.data?.error ||
          "Failed to update Lusha flag. Please try again.",
      );
    } finally {
      setIsLushaLoading(false);
    }
  };

  // Prevent leaving the page while contacts are being requested
  useEffect(() => {
    if (!isLushaLoading) return;

    const blockUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    const blockLinks = (e) => {
      if (e.target.closest("a[href]")) {
        e.preventDefault();
        e.stopPropagation();
        toastr.warning("Please wait until the contact request completes.");
      }
    };

    window.addEventListener("beforeunload", blockUnload);
    document.addEventListener("click", blockLinks, true);
    return () => {
      window.removeEventListener("beforeunload", blockUnload);
      document.removeEventListener("click", blockLinks, true);
    };
  }, [isLushaLoading]);

  return (
    <>
      {draftFor && draftFor.kind === "source" && (
        <SourceAnalysis
          key={`source-${draftFor.candidate?.candidate_id}`}
          role_id={id}
          candidate={draftFor.candidate}
          onClose={() => setDraftFor(null)}
          // A resume or dossier made from the panel gets its R or D in the
          // grid straight away.
          onFilesChanged={() => fetchData(true)}
          // The panel's autosave keeps this candidate's Consultant Notes
          // cell current, without reloading the grid.
          onNoteSaved={(body, saved_at) => {
            const candidateId = draftFor.candidate?.candidate_id;
            setData((prev) =>
              prev.map((r) =>
                r.candidate_id === candidateId
                  ? {
                      ...r,
                      consultant_notes: body,
                      consultant_notes_saved_at: saved_at,
                    }
                  : r,
              ),
            );
          }}
        />
      )}

      {videoFor && (
        <CandidateVideo
          key={`video-${videoFor.candidate_id}`}
          role_id={id}
          candidate={videoFor}
          onClose={() => {
            setVideoFor(null);
            // A video just started (or just finished) - the row's badge and
            // the 20 s checks follow from this.
            loadVideos();
          }}
        />
      )}

      {draftFor && draftFor.kind !== "source" && (
        <OutreachDraft
          key={`${draftFor.kind}-${draftFor.candidate?.candidate_id}`}
          role_id={id}
          kind={draftFor.kind}
          candidate={draftFor.candidate}
          onClose={() => setDraftFor(null)}
          onSent={(kind, channel, sent_at) =>
            markSent(draftFor.candidate?.candidate_id, kind, channel, sent_at)
          }
        />
      )}

      {consentModal && (
        <ConsentModal
          onClose={(status) => {
            if (status == 1) {
              fetchData();
            }
            setConsentModal(false);
          }}
          candidate_id={consentCandidateid}
        />
      )}
      {showPhoneModal && (
        <Suspense fallback={<div>Loading Contact Modal...</div>}>
          <modals.LushaModal
            key={`${lushaPhone}-${lushaEmail}`}
            show={showPhoneModal}
            isRequesting={isLushaLoading}
            phoneNumbers={lushaPhone}
            email={lushaEmail}
            data={CandidateData}
            created={lastChecked} // 👈 pass lastChecked (updated || created)
            lushaFlag={lushaFlag}
            token={token}
            onClose={(e) => {
              setShowPhoneModal(false);
              fetchData();

              setLushaCandidateId(null);
            }}
            onRequestAgain={() => handleUpdateLushaFlag()}
          />
        </Suspense>
      )}
      <div className="content">
        <div className="row align-items-center mb-4">
          <div className="d-md-flex d-sm-block justify-content-between align-items-center flex-wrap">
            <div className="my-auto mb-2">
              <h2 className="mb-1">{roleDetails[0]?.name}</h2>
            </div>
            {/* ms-auto keeps the buttons on the right even when the row runs
                out of width and they wrap onto their own line - on that line
                justify-content-between has nothing to push them against. */}
            <div className="d-flex flex-wrap gap-2 mt-3 ms-auto justify-content-end">
              <button
                className="btn btn-sm btn-info"
                variant={wrapText ? "contained" : "outlined"}
                onClick={() => setWrapText((prev) => !prev)}
              >
                {wrapText ? "Unwrap Text" : "Wrap Text"}
              </button>
              <button
                className="btn btn-sm btn-success"
                onClick={() => setShowDeckModal(true)}
              >
                <i className="fa-solid fa-file-powerpoint me-1"></i> Generate
                Deck
              </button>
              {showDeckModal && (
                process.env.REACT_APP_STATUS_UPDATE_PROVIDER === "claude" ? (
                  <ClaudeGenerateDeckModal
                    role_id={id}
                    role_name={roleDetails[0]?.name}
                    token={token}
                    onClose={() => setShowDeckModal(false)}
                  />
                ) : (
                  <GenerateDeckModal
                    role_id={id}
                    role_name={roleDetails[0]?.name}
                    token={token}
                    onClose={() => setShowDeckModal(false)}
                  />
                )
              )}
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => setShowDecksModal(true)}
              >
                <i className="fa-solid fa-clock-rotate-left me-1"></i> Generated
                Documents
              </button>
              {showDecksModal && (
                <GeneratedDocumentsModal
                  role_id={id}
                  token={token}
                  onClose={() => setShowDecksModal(false)}
                />
              )}
              <button
                className="btn btn-sm btn-primary"
                onClick={() => {
                  if (selectedRows.length > 1) {
                    toastr.warning(
                      "Please select only one candidate to generate the dossier.",
                    );
                    return;
                  }

                  if (selectedRows.length === 0) {
                    toastr.warning("Please select a candidate");
                    return;
                  }

                  setShowDossierModal(true);
                }}
              >
                Create Dossier
              </button>

              <button
                className="btn btn-primary btn-sm d-inline-flex align-items-center"
                onClick={() => setShowPptModal(true)}
                disabled={isRefreshing}
              >
                {isRefreshing ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-1"
                      role="status"
                      aria-hidden="true"
                    />
                    <span>Refreshing...</span>
                  </>
                ) : (
                  "Profiling"
                )}
              </button>

              {showPptModal && (
                <PptModal
                  onClose={() => setShowPptModal(false)}
                  onCreateUnmasked={() => generatePPT(false)}
                  onCreateMasked={() => generatePPT(true)}
                />
              )}
              {showDossierModal && (
                <GenerateDossierModal
                  show={showDossierModal}
                  onClose={() => setShowDossierModal(false)}
                  candidateId={selectedRows[0]}
                  role_id={id}
                  role_name={roleDetails[0]?.name}
                />
              )}
              {/* <button
                className="btn btn-primary btn-sm d-inline-flex align-items-center"
                onClick={handleCreatePPT}
                disabled={loading || selectedRows.length === 0}
              >
                {loading ? (
                  <>
                    <i className="ti ti-loader me-1"></i> Creating...
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-file-powerpoint me-1"></i> Create
                    PPT
                  </>
                )}
              </button> */}
              <Link
                className="btn btn-primary btn-sm d-inline-flex align-items-center"
                to={`/role/funnel/${id}`} state={{ returnTo: `/workstation/view/${id}` }}
              >
                <i className="ti ti-chart-bar me-1"></i> Role Analysis
              </Link>
              <Link
                className="btn btn-primary btn-sm d-inline-flex align-items-center"
                to={`/role/sendRecommendedCandidate/${id}`}
              >
                <i className="ti ti-chart-bar me-1"></i> AI Candidates
              </Link>
              {BulkAddLinkedinUrlModel && (
                <BulkAddLinkedinUrl
                  role_id={id}
                  onClose={() => {
                    setBulkAddLinkedinUrlModel(false);
                    fetchData();
                  }}
                />
              )}
              <Link
                className="btn btn-primary btn-sm d-inline-flex align-items-center"
                onClick={() => setBulkAddLinkedinUrlModel(true)}
              >
                <i className="fa-solid fa-circle-plus me-1"></i> Linkedin URL
              </Link>
              {importFromExcelModal && (
                <ImportFromExcel
                  role_id={id}
                  onClose={() => {
                    setImportFromExcelModal(false);
                    fetchData();
                  }}
                />
              )}
              <Link
                className="btn btn-primary btn-sm d-inline-flex align-items-center"
                onClick={() => setImportFromExcelModal(true)}
              >
                <i className="fa-solid fa-file-excel me-1"></i> Import Excel
              </Link>
              <Link
                className="btn btn-primary btn-sm d-inline-flex align-items-center"
                to={`/workstation/search_universe/${id}`}
              >
                <i className="fa-solid fa-globe me-1"></i> Search Universe
              </Link>
              <Link
                className="btn btn-primary btn-sm d-inline-flex align-items-center"
                to={`/workstation/search_map/${id}`} state={{ returnTo: `/workstation/view/${id}` }}
              >
                <i className="fa-solid fa-sitemap me-1"></i> Search Map &amp; Gap
                Analysis
              </Link>
              <button
                className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center"
                onClick={handleBack}
              >
                <i className="ti ti-arrow-left me-1"></i> Back
              </button>
            </div>
          </div>
        </div>
        {showEditStatusModal && (
          <EditStatus
            candidate_id={selectedCandidate.candidate_id}
            role_id={id}
            minor_status={selectedMinorStatus}
            onClose={async (status) => {
              try {
                setGlobalLoading(true);
                await fetchData();
              } catch (err) {
                console.error(err);
                setGlobalLoading(false);
              } finally {
                setGlobalLoading(false);
                setShowEditStatusModal(false);
              }
            }}
          />
        )}
        <div className="card">
          <div className="card-body p-0">
            <div className="custom-datatable-filter table-responsive">
              <MaterialReactTable table={table} />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
